/**
 * Assistente do Google Calendar no Console
 * (SPEC-20261027-2350-integracao-google-calendar) — a parte pura, para testar:
 * em que pé está cada passo e os links do Google Cloud.
 */

export type Passo = 'projeto' | 'api' | 'consentimento' | 'credencial' | 'teste' | 'habilitar' | 'verificacao'
export type SituacaoDoPasso = 'pendente' | 'feito' | 'verificado' | 'erro' | 'aguardando'

export const PASSOS: readonly Passo[] = ['projeto', 'api', 'consentimento', 'credencial', 'teste', 'habilitar', 'verificacao']

type Situacao = {
  etapas: { projeto: boolean; api: boolean; consentimento: boolean; verificacaoEnviada?: boolean; verificado?: boolean }
  credenciaisOkEm: string | null
  testeOkEm: string | null
  testeErro: string | null
  habilitado: boolean
}

/**
 * Passos 1–3 o Orbind não consegue conferir sozinho (é tudo dentro do Google):
 * ficam "feito" quando a pessoa confirma e viram "conferido" quando o teste do
 * passo 5 passa — o teste só funciona se eles estiverem certos.
 */
export function estadoDosPassos(s: Situacao): { passos: Record<Passo, SituacaoDoPasso>; atual: Passo; concluidos: number } {
  const testado = s.testeOkEm !== null
  const manual = (feito: boolean): SituacaoDoPasso => (testado ? 'verificado' : feito ? 'feito' : 'pendente')
  const passos: Record<Passo, SituacaoDoPasso> = {
    projeto: manual(s.etapas.projeto),
    api: manual(s.etapas.api),
    consentimento: manual(s.etapas.consentimento),
    credencial: s.credenciaisOkEm ? 'verificado' : 'pendente',
    teste: testado ? 'verificado' : s.testeErro ? 'erro' : 'pendente',
    habilitar: s.habilitado ? 'feito' : 'pendente',
    // passo 7: enviado = esperando a análise do Google; aprovado = feito
    verificacao: s.etapas.verificado ? 'feito' : s.etapas.verificacaoEnviada ? 'aguardando' : 'pendente',
  }
  const concluidos = PASSOS.filter((p) => passos[p] === 'feito' || passos[p] === 'verificado').length
  const atual = PASSOS.find((p) => passos[p] === 'pendente' || passos[p] === 'erro' || passos[p] === 'aguardando') ?? 'verificacao'
  return { passos, atual, concluidos }
}

/**
 * Número do projeto a partir do ID do cliente (`123456789012-abc….apps.googleusercontent.com`).
 * É a fonte mais confiável de "qual projeto": a chave só existe naquele projeto,
 * enquanto o ID digitado no passo 1 pode ser de outro (o erro mais comum de quem
 * configura — a API ativada num projeto e a chave criada em outro).
 */
export function numeroDoProjeto(clientId: string): string | null {
  return /^(\d{6,})-/.exec(clientId.trim())?.[1] ?? null
}

/** ID de projeto do Google: 6 a 30 letras minúsculas, números e hífens, começando com letra. */
export const idDoProjetoValido = (id: string): boolean => /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(id.trim())

/** Projeto para os links: o da chave (passo 4) quando já existe; senão o informado no passo 1. */
export const projetoDosLinks = (projectId: string, clientId: string): string => numeroDoProjeto(clientId) ?? projectId

/** Páginas do Google Cloud — já no projeto certo quando ele é conhecido. */
export function linksDoGoogle(projectId: string) {
  const p = projectId.trim() ? `?project=${encodeURIComponent(projectId.trim())}` : ''
  const base = 'https://console.cloud.google.com'
  return {
    criarProjeto: `${base}/projectcreate`,
    ativarApi: `${base}/apis/library/calendar-json.googleapis.com${p}`,
    consentimento: `${base}/auth/overview${p}`,
    acessoADados: `${base}/auth/scopes${p}`,
    publico: `${base}/auth/audience${p}`,
    criarCliente: `${base}/auth/clients/create${p}`,
    identidadeVisual: `${base}/auth/branding${p}`,
    centralDeVerificacao: `${base}/auth/verification${p}`,
    searchConsole: 'https://search.google.com/search-console',
    // página "Verificação de propriedade" do domínio: diz se ele já está verificado
    donoDoDominio: `https://search.google.com/search-console/ownership?resource_id=sc-domain%3A${PAGINAS_DO_SITE.dominio}`,
  }
}

/** Endereços públicos que o Google pede na verificação (páginas do site). */
export const PAGINAS_DO_SITE = {
  inicial: 'https://orbind.com.br',
  privacidade: 'https://orbind.com.br/privacidade',
  termos: 'https://orbind.com.br/termos',
  dominio: 'orbind.com.br',
} as const

/** Justificativa do escopo, em inglês (a análise do Google é em inglês). */
export const JUSTIFICATIVA_DO_ESCOPO =
  'Orbind is a work management platform. Each user can optionally connect their own Google account in the Orbind Calendar. ' +
  'We use calendar.events to (1) show the events of the user\'s primary Google Calendar inside the Orbind Calendar, visible only to that user, ' +
  'and (2) when the user checks "Also send to my Google Calendar", create, update and delete in the user\'s calendar the events that the user created in Orbind. ' +
  'Event data is not stored in our database (only kept in memory for up to 1 minute), is never shared with other users, and is not used for advertising or AI training. ' +
  'The narrower calendar.events.owned scope is not enough because users must see all events of their primary calendar, including events created by others.'
